import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useToast } from './ToastProvider';
import { useTranslation } from 'react-i18next';

export default function SavedOffersPage() {
  const { t } = useTranslation();
  const [offers, setOffers] = useState([]);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    fetch('/api/offers')
      .then(res => res.json())
      .then(data => setOffers(data))
      .catch(() => setOffers([]));
  }, []);

  const deleteOffer = async (id) => {
    try {
      const res = await fetch(`/api/offers/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setOffers(offers.filter(o => o.id !== id));
      }
    } catch (err) {
      // Hata yönetimi eklenebilir
    }
  };

  const viewOffer = (offer) => {
    localStorage.setItem("selectedOfferId", offer.id);
    localStorage.setItem("selectedOfferMode", "view");
    navigate("/dash");
  };
  const editOffer = (offer) => {
    localStorage.setItem("selectedOfferId", offer.id);
    localStorage.setItem("selectedOfferMode", "edit");
    navigate("/dash");
  };

  const OFFER_STATUSES = [
    { value: 'Taslak', color: 'bg-gray-200 text-gray-700' },
    { value: 'Gönderildi', color: 'bg-blue-100 text-blue-800' },
    { value: 'Kabul Edildi', color: 'bg-green-100 text-green-800' },
    { value: 'Reddedildi', color: 'bg-red-100 text-red-700' },
  ];

  const updateOfferStatus = (id, newStatus) => {
    const updated = offers.map(o => o.id === id ? { ...o, status: newStatus } : o);
    localStorage.setItem('offers', JSON.stringify(updated));
    setOffers(updated);
  };

  // Excel dışa aktarım fonksiyonu
  const exportToExcel = () => {
    if (offers.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(offers.map(o => ({
      [t('Teklif No')]: o.offerNo,
      [t('Tarih')]: o.date,
      [t('Alıcı')]: o.recipient,
      [t('Durum')]: t(o.status),
      [t('Not')]: o.note,
      [t('Alt Toplam')]: o.subtotal,
      [t('KDV')]: o.totalVAT,
      [t('Genel Toplam')]: o.grandTotal,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('Teklifler'));
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'teklifler.xlsx');
  };
  // CSV dışa aktarım fonksiyonu
  const exportToCSV = () => {
    if (offers.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(offers.map(o => ({
      [t('Teklif No')]: o.offerNo,
      [t('Tarih')]: o.date,
      [t('Alıcı')]: o.recipient,
      [t('Durum')]: t(o.status),
      [t('Not')]: o.note,
      [t('Alt Toplam')]: o.subtotal,
      [t('KDV')]: o.totalVAT,
      [t('Genel Toplam')]: o.grandTotal,
    })));
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'teklifler.csv');
  };

  const handleBackup = () => {
    if (offers.length === 0) {
      toast.showToast(t('Yedeklenecek teklif yok!'), 'warning');
      return;
    }
    const blob = new Blob([JSON.stringify(offers, null, 2)], { type: 'application/json' });
    saveAs(blob, 'teklifler_yedek.json');
    toast.showToast(t('Yedek dosyası indirildi.'), 'success');
  };
  const handleRestore = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = JSON.parse(evt.target.result);
        if (!Array.isArray(data)) throw new Error('Geçersiz dosya!');
        localStorage.setItem('offers', JSON.stringify(data));
        setOffers(data);
        toast.showToast(t('Yedek başarıyla yüklendi.'), 'success');
      } catch {
        toast.showToast(t('Geçersiz veya bozuk dosya!'), 'error');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="w-full p-6">
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <button onClick={exportToExcel} className="px-4 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 transition text-sm">{t("Excel'e Aktar")}</button>
        <button onClick={exportToCSV} className="px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition text-sm">{t("CSV'ye Aktar")}</button>
        <button onClick={handleBackup} className="px-4 py-2 rounded bg-yellow-400 text-blue-900 font-semibold hover:bg-yellow-500 transition text-sm">{t('Yedekle (JSON)')}</button>
        <label className="px-4 py-2 rounded bg-gray-200 text-blue-900 font-semibold hover:bg-gray-300 transition text-sm cursor-pointer">
          {t('Geri Yükle')}
          <input type="file" accept="application/json" className="hidden" onChange={handleRestore} />
        </label>
      </div>
      <h1 className="text-3xl font-bold mb-8 text-blue-900 flex items-center gap-3 px-6 pt-6">
        <svg className="w-8 h-8 text-blue-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2a2 2 0 012-2h2a2 2 0 012 2v2m-6 4h6a2 2 0 002-2v-6a2 2 0 00-2-2h-2a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>
        {t('Kayıtlı Teklifler')}
      </h1>
      {offers.length === 0 ? (
        <div className="text-gray-500">{t('Kayıtlı teklif yok.')}</div>
      ) : (
        <div className="w-full">
          {/* Başlık */}
          <div className="hidden sm:grid grid-cols-9 gap-0 bg-gradient-to-r from-blue-100 to-blue-50 shadow-sm rounded-t-xl text-xs font-bold text-blue-950 px-4 py-3 mb-1">
            <div className="col-span-1 flex items-center">{t('Teklif No')}</div>
            <div className="col-span-1 flex items-center">{t('Tarih')}</div>
            <div className="col-span-2 flex items-center">{t('Alıcı')}</div>
            <div className="col-span-1 flex items-center">{t('Durum')}</div>
            <div className="hidden sm:flex col-span-2 items-center">{t('Not')}</div>
            <div className="hidden sm:flex col-span-1 items-center">{t('E-posta Durumu')}</div>
            <div className="col-span-1 flex items-center">{t('İşlemler')}</div>
          </div>
          <div className="flex flex-col gap-y-2">
            {offers.map(offer => (
              <div key={offer.id}
                className="sm:grid sm:grid-cols-9 gap-0 bg-white shadow rounded-xl px-4 py-3 transition hover:bg-blue-50 items-center flex flex-col sm:flex-none"
              >
                {/* Teklif No */}
                <div className="sm:col-span-1 text-xs font-bold text-blue-900 truncate flex items-center">{offer.offerNo}</div>
                {/* Tarih */}
                <div className="sm:col-span-1 text-gray-500 text-xs truncate flex items-center">{offer.date}</div>
                {/* Alıcı */}
                <div className="sm:col-span-2 font-medium text-gray-800 text-xs truncate flex items-center max-w-[120px] sm:max-w-full">{offer.recipient}</div>
                {/* Durum */}
                <div className="sm:col-span-1 flex flex-col sm:flex-row gap-1 items-start sm:items-center">
                  {OFFER_STATUSES.map(s => (
                    <button
                      key={s.value}
                      className={`px-2 py-1 rounded-full text-xs font-semibold border transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${offer.status === s.value ? s.color + ' ring-2 ring-blue-200 border-blue-300' : 'bg-white text-gray-500 border-gray-200 hover:bg-blue-50'}`}
                      onClick={() => updateOfferStatus(offer.id, s.value)}
                      type="button"
                    >
                      {t(s.value)}
                    </button>
                  ))}
                </div>
                {/* Not */}
                <div className="hidden sm:flex sm:col-span-2 text-gray-600 text-xs items-center truncate">{offer.note || '-'}</div>
                {/* E-posta Durumu */}
                <div className="hidden sm:flex sm:col-span-1 items-center text-xs font-semibold">
                  <span className={offer.emailSent ? 'text-green-700' : 'text-gray-400'}>
                    {offer.emailSent ? t('E-posta ile gönderildi') : t('E-posta gönderilmedi')}
                  </span>
                </div>
                {/* İşlemler */}
                <div className="sm:col-span-1 flex gap-1 items-center mt-2 sm:mt-0">
                  <button className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition" onClick={() => viewOffer(offer)}>{t('Görüntüle')}</button>
                  <button className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition" onClick={() => editOffer(offer)}>{t('Düzenle')}</button>
                  <button className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition" onClick={() => deleteOffer(offer.id)}>{t('Sil')}</button>
                </div>
                {/* Mobilde özet bilgi (Not ve E-posta Durumu) */}
                <div className="sm:hidden flex flex-col gap-0.5 mt-2 text-[11px] text-gray-500 w-full">
                  <div><span className="font-semibold text-gray-700">{t('Not')}:</span> {offer.note ? offer.note.slice(0, 20) + (offer.note.length > 20 ? '...' : '') : '-'}</div>
                  <div><span className="font-semibold text-gray-700">{t('E-posta')}:</span> <span className={offer.emailSent ? 'text-green-700' : 'text-gray-400'}>{offer.emailSent ? t('Gönderildi') : t('Gönderilmedi')}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
} 